/**
 * Native modal overlay wrapper — replaces HeroUI v3 Modal which
 * doesn't render a proper backdrop/centered overlay.
 *
 * Usage: Replace <Modal isOpen={x}><ModalDialog>...</ModalDialog></Modal>
 * with <ModalOverlay isOpen={x} onClose={fn}>...</ModalOverlay>
 */
import React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@heroui/react";

// Track modal depth for stacking nested modals
let modalCount = 0;

// Stack of open-modal close handlers. The topmost entry is the modal that
// should respond to Escape, regardless of where focus currently lives.
const escapeHandlerStack: Array<() => void> = [];

interface ModalOverlayProps {
  isOpen: boolean;
  onClose?: () => void;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "full";
  isDismissable?: boolean;
  /** Accessible name for the dialog (WCAG SC 4.1.2). Falls back to the localised default. */
  ariaLabel?: string;
  /** Id of the element labelling the dialog (e.g. ModalHeader). Takes precedence over ariaLabel. */
  ariaLabelledBy?: string;
  /**
   * Scroll behaviour for content that exceeds the modal's bounds.
   * - "outside" (default): the dialog itself scrolls (existing behaviour).
   * - "inside": the dialog is a bounded flex column; the consumer is
   *   responsible for making one child (typically ModalBody) scroll.
   *   Use this for modals that host their own scrollable content
   *   (iframes, previewers, viewers) so only one scrollbar is visible.
   */
  scrollBehavior?: "inside" | "outside";
}

const sizeClasses: Record<string, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  full: "max-w-[calc(100vw-2rem)]",
};

export const ModalOverlay: React.FC<ModalOverlayProps> = ({
  isOpen,
  onClose,
  onOpenChange,
  children,
  className,
  size = "lg",
  isDismissable = true,
  ariaLabel,
  ariaLabelledBy,
  scrollBehavior = "outside",
}) => {
  const { t } = useTranslation();
  const zIndexRef = React.useRef(0);
  const dialogRef = React.useRef<HTMLDivElement>(null);

  // Latest handleClose via ref so the document-level Escape listener always
  // sees current props without needing to re-attach on every render.
  const handleCloseRef = React.useRef<() => void>(() => {});

  handleCloseRef.current = () => {
    onClose?.();
    onOpenChange?.(false);
  };

  React.useEffect(() => {
    if (isOpen) {
      modalCount++;
      zIndexRef.current = 9999 + modalCount;
    }

    return () => {
      if (isOpen) {
        modalCount = Math.max(0, modalCount - 1);
      }
    };
  }, [isOpen]);

  // Focus the dialog on open so Escape works without a prior click.
  // Skip if a child (e.g. an autoFocus input) already owns focus — otherwise
  // we'd steal it back on every render and break typing.
  React.useEffect(() => {
    if (!isOpen) return;
    const el = dialogRef.current;

    if (el && !el.contains(document.activeElement)) {
      el.focus();
    }
  }, [isOpen]);

  // Document-level Escape handler. Using React's onKeyDown misses Escape when
  // focus leaves the dialog (e.g. native <select> dropdowns, toggle switches).
  // A stack ensures only the topmost modal closes when nested.
  React.useEffect(() => {
    if (!isOpen) return;

    const close = () => handleCloseRef.current();

    escapeHandlerStack.push(close);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const top = escapeHandlerStack[escapeHandlerStack.length - 1];

      if (top === close) {
        e.stopPropagation();
        close();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      const idx = escapeHandlerStack.indexOf(close);

      if (idx !== -1) escapeHandlerStack.splice(idx, 1);
    };
  }, [isOpen]);

  const zIndex = zIndexRef.current || 10000;

  const handleClose = () => handleCloseRef.current();

  if (!isOpen) return null;

  const modal = (
    <div
      ref={dialogRef}
      className="fixed inset-0 flex items-center justify-center"
      role="presentation"
      style={{ zIndex }}
      tabIndex={-1}
    >
      {/* Backdrop */}
      {isDismissable ? (
        <button
          aria-label={t("Close modal")}
          className="absolute inset-0 bg-foreground/40 cursor-default"
          type="button"
          onClick={handleClose}
        />
      ) : (
        <div aria-hidden="true" className="absolute inset-0 bg-foreground/40" />
      )}
      {/* Dialog */}
      <div
        aria-label={ariaLabelledBy ? undefined : (ariaLabel ?? t("Dialog"))}
        aria-labelledby={ariaLabelledBy}
        aria-modal="true"
        className={cn(
          "relative z-10 w-full bg-content1 text-content1-foreground rounded-xl shadow-2xl",
          scrollBehavior === "inside"
            ? "h-[85vh] flex flex-col overflow-hidden"
            : "max-h-[85vh] overflow-y-auto",
          sizeClasses[size] || sizeClasses.lg,
          className,
        )}
        role="dialog"
      >
        {/* Close button */}
        <button
          aria-label={t("Close")}
          className={cn(
            "p-1 rounded-full hover:bg-default-100 text-default-500 hover:text-foreground transition-colors z-20",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-default-400",
            scrollBehavior === "inside"
              ? "absolute top-3 right-3"
              : "sticky top-3 float-right mr-3 mt-3",
          )}
          type="button"
          onClick={handleClose}
        >
          <svg
            fill="none"
            height="20"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            width="20"
          >
            <line x1="18" x2="6" y1="6" y2="18" />
            <line x1="6" x2="18" y1="6" y2="18" />
          </svg>
        </button>
        {children}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export const ModalHeader: React.FC<{
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  id?: string;
  "data-id"?: string;
}> = ({ children, className, id, ...rest }) => (
  <div
    className={cn("px-6 pt-6 pb-2 text-lg font-semibold", className)}
    id={id ?? rest["data-id"]}
    {...rest}
  >
    {children}
  </div>
);

export const ModalBody: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => <div className={cn("px-6 py-4 flex-1 min-h-0 overflow-y-auto", className)}>{children}</div>;

export const ModalFooter: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => (
  <div className={cn("px-6 pb-6 pt-2 flex justify-end gap-2 shrink-0", className)}>{children}</div>
);
